import Subscription from '@aytm/lib/common/subscription'
import DevLog from '../devlog/Module'
import detectDevice from './detectDevice'

import { ModuleID } from '../types'
import {
  DEFAULT_WIDGET_WIDTH,
  IViewport,
  IViewportModule,
  IViewportPaddings,
  IViewportProps,
  ViewportDevice,
  ViewportOrientation,
  ViewportScreenType
} from './types'

const OnUpdateSubscription = Subscription<IViewport>()

const root: { element?: HTMLElement; resizeObserver?: ResizeObserver } = {}

const DEFAULT_PADDINGS: IViewportPaddings = Object.freeze({
  top: 0,
  right: 0,
  bottom: 0,
  left: 0
})

let moduleLoaded = false

let emulated = false

const buildViewportReader = (props: IViewportProps, emulated: boolean): IViewport => {
  const reader: IViewport = {
    onUpdate(listener) {
      return OnUpdateSubscription.listen(listener)
    },
    get width() {
      return props.width
    },
    get height() {
      return props.height
    },
    get autoHeight() {
      return Boolean(props.autoHeight)
    },
    get maxAutoHeight() {
      return props.maxAutoHeight
    },
    updateAutoHeight(height) {
      if (props.autoHeight && props.height !== height) {
        DevLog._log('SW[viewport]', 'update auto height', height)
        props.height = height
        OnUpdateSubscription.update(reader)
      }
    },
    get scale() {
      return null == props.scale ? 1 : props.scale
    },
    get mobile() {
      return Boolean(props.mobile)
    },
    get tablet() {
      return Boolean(props.tablet)
    },
    get screenType() {
      return props.mobile
        ? ViewportScreenType.MOBILE
        : props.tablet
        ? ViewportScreenType.TABLET
        : ViewportScreenType.DESKTOP
    },
    get device() {
      return props.mobile ? ViewportDevice.MOBILE : ViewportDevice.DESKTOP
    },
    get widgetWidth() {
      switch (true) {
        case props.mobile:
          return DEFAULT_WIDGET_WIDTH.MOBILE
        case props.tablet:
          return Math.min(
            DEFAULT_WIDGET_WIDTH.TABLET,
            props.width - (props.paddings?.left || 0) - (props.paddings?.right || 0)
          )
        default:
          return Math.min(
            DEFAULT_WIDGET_WIDTH.DESKTOP,
            props.width - (props.paddings?.left || 0) - (props.paddings?.right || 0)
          )
      }
    },
    get orientation() {
      return props.orientation || ViewportOrientation.LANDSCAPE
    },
    get touch() {
      return Boolean(props.touch)
    },
    get paddings() {
      return props.paddings || DEFAULT_PADDINGS
    },
    get emulated() {
      return emulated
    },
    get emulationMobileFrame() {
      return props.emulationMobileFrame
    },
    get emulationNoScrollBar() {
      return props.emulationNoScrollBar
    },
    get emulationMobileCursor() {
      return props.emulationMobileCursor
    },
    get emulationScrollContainer() {
      return props.emulationScrollContainer
    }
  }
  return reader
}

const realViewportProps: IViewportProps = {
  width: 0,
  height: 0,
  autoHeight: false,
  maxAutoHeight: null,
  scale: 1,
  mobile: false,
  tablet: false,
  orientation: ViewportOrientation.LANDSCAPE,
  touch: false,
  paddings: DEFAULT_PADDINGS,
  emulationMobileFrame: false,
  emulationNoScrollBar: false,
  emulationMobileCursor: false,
  emulationScrollContainer: false
}
const realViewportReader: IViewport = buildViewportReader(realViewportProps, false)

const emulatedViewportProps: IViewportProps = Object.assign({}, realViewportProps)
const emulatedViewportReader: IViewport = buildViewportReader(emulatedViewportProps, true)

const ViewportModule: IViewportModule = {
  get id() {
    return ModuleID.VIEWPORT
  },
  config({ emulate } = {}) {
    if (emulate) {
      ViewportModule.emulate(emulate)
    }
    return ViewportModule
  },
  async load() {
    if (!ViewportModule.loaded) {
      moduleLoaded = true
    }
    return Promise.resolve(ViewportModule)
  },
  get loaded() {
    return moduleLoaded
  },
  set root(thisRoot) {
    if (root.element !== thisRoot) {
      if (root.resizeObserver) {
        root.resizeObserver.disconnect()
      }
      if (thisRoot) {
        if (null == root.resizeObserver) {
          root.resizeObserver =
            'undefined' !== typeof ResizeObserver
              ? new ResizeObserver(() => {
                  setTimeout(updateRealViewport, 0)
                })
              : void 0
          if (root.resizeObserver) {
            root.resizeObserver.observe(thisRoot)
          }
        }
        if (null == root.element && 'undefined' !== typeof window) {
          if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', updateRealViewport)
          } else {
            window.removeEventListener('resize', updateRealViewport)
          }
        }
      } else {
        if ('undefined' !== typeof window) {
          if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', updateRealViewport)
          } else {
            window.addEventListener('resize', updateRealViewport)
          }
        }
      }
      root.element = thisRoot
      updateRealViewport()
    }
  },
  get root() {
    return root.element
  },
  get rootObserved() {
    return Boolean(null == root.element || (root.element && root.resizeObserver))
  },
  get viewport() {
    return emulated ? emulatedViewportReader : realViewportReader
  },
  get realViewport() {
    return realViewportReader
  },
  applyPaddings(withPaddings = {}) {
    const currentViewportProps = emulated ? emulatedViewportProps : realViewportProps
    const clonedPaddings = Object.freeze({ ...currentViewportProps.paddings, ...withPaddings })
    currentViewportProps.paddings = clonedPaddings
    return ViewportModule.update()
  },
  emulate(viewportProps) {
    if (viewportProps) {
      Object.assign(emulatedViewportProps, viewportProps)
      if (null == viewportProps.scale) {
        emulatedViewportProps.scale = 1
      }
      if (null == viewportProps.touch) {
        emulatedViewportProps.touch = Boolean(viewportProps.mobile || viewportProps.tablet)
      }
      if (null == viewportProps.paddings) {
        emulatedViewportProps.paddings = DEFAULT_PADDINGS
      }
      emulated = true
      DevLog._moduleLog(ViewportModule, 'emulate', { ...emulatedViewportProps })
      OnUpdateSubscription.update(emulatedViewportReader)
    } else if (emulated) {
      emulated = false
      DevLog._moduleLog(ViewportModule, 'stop emulation')
      OnUpdateSubscription.update(realViewportReader)
    }
    return ViewportModule
  },
  get emulated() {
    return emulated
  },
  update() {
    updateRealViewport()
    return ViewportModule
  },
  onUpdate(listener) {
    return OnUpdateSubscription.listen(listener)
  }
}

function updateRealViewport() {
  const updatedViewport: Partial<IViewportProps> = {}
  const paddings = realViewportProps.paddings || DEFAULT_PADDINGS
  if (root.element) {
    // update viewport to container
    const hPaddings = paddings.left + paddings.right
    const vPaddings = paddings.top + paddings.bottom
    updatedViewport.width = root.element.clientWidth - hPaddings
    updatedViewport.height = root.element.clientHeight - vPaddings
    updatedViewport.orientation =
      updatedViewport.width > updatedViewport.height ? ViewportOrientation.LANDSCAPE : ViewportOrientation.PORTRAIT
  } else {
    // update viewport to window
    const hPaddings = paddings.left + paddings.right
    const vPaddings = paddings.top + paddings.bottom
    if (window.visualViewport) {
      updatedViewport.width = window.visualViewport.width - hPaddings
      updatedViewport.height = window.visualViewport.height - vPaddings
      updatedViewport.scale = window.visualViewport.scale
    } else {
      updatedViewport.width = window.innerWidth - hPaddings
      updatedViewport.height = window.innerHeight - vPaddings
    }

    const landscape = window.matchMedia('(orientation: landscape)')
    updatedViewport.orientation = landscape.matches ? ViewportOrientation.LANDSCAPE : ViewportOrientation.PORTRAIT
  }

  let updated = false
  for (const key of Object.keys(updatedViewport) as (keyof IViewportProps)[]) {
    const value = (updatedViewport as IViewportProps)[key]
    if (realViewportProps[key] !== value) {
      Object.assign(realViewportProps, updatedViewport)
      updated = true
      break
    }
  }
  if (updated) {
    DevLog._moduleLog(ViewportModule, 'update real', { ...realViewportProps })
    OnUpdateSubscription.update(emulated ? emulatedViewportReader : realViewportReader)
  }
}

if ('undefined' !== typeof document && 'undefined' !== typeof window) {
  const deviceMatch = detectDevice()
  realViewportProps.mobile = deviceMatch.mobile
  realViewportProps.tablet = deviceMatch.tablet

  realViewportProps.touch = navigator?.maxTouchPoints > 0

  ViewportModule.root = null
}

OnUpdateSubscription.catch(error => {
  DevLog.error(error, `[module ${ViewportModule.id}] update listener`)
})

export default ViewportModule
